import React, { useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Download,
  Calendar,
  ChevronRight,
  User,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDateTime } from '../../lib/utils';

const AuditLogsTab = ({ api, users, auditLogs, auditActions, setAuditLogs }) => {
  const [auditFilter, setAuditFilter] = useState({
    action: 'all',
    userId: 'all',
    startDate: '',
    endDate: ''
  });

  const fetchAuditLogs = async () => {
    try {
      const params = new URLSearchParams();
      if (auditFilter.action !== 'all') params.append('action', auditFilter.action);
      if (auditFilter.userId !== 'all') params.append('user_id', auditFilter.userId);
      if (auditFilter.startDate) params.append('start_date', auditFilter.startDate);
      if (auditFilter.endDate) params.append('end_date', auditFilter.endDate);
      
      const response = await api().get(`/admin/audit-logs?${params.toString()}`);
      setAuditLogs(response.data.logs || response.data);
    } catch (error) {
      toast.error('Failed to load audit logs');
    }
  };

  const handleExportAuditLogs = async () => {
    try {
      const params = new URLSearchParams();
      if (auditFilter.action !== 'all') params.append('action', auditFilter.action);
      if (auditFilter.userId !== 'all') params.append('user_id', auditFilter.userId);
      if (auditFilter.startDate) params.append('start_date', auditFilter.startDate);
      if (auditFilter.endDate) params.append('end_date', auditFilter.endDate);
      
      const response = await api().get(`/admin/audit-logs/export?${params.toString()}`, {
        responseType: 'blob'
      });
      
      // Download the file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit_logs_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Audit logs exported');
    } catch (error) {
      toast.error('Failed to export audit logs');
    }
  };

  const getActionBadgeColor = (action) => {
    if (action?.includes('create') || action?.includes('CREATE')) return 'bg-emerald-50 text-emerald-700';
    if (action?.includes('delete') || action?.includes('DELETE')) return 'bg-red-50 text-red-700';
    if (action?.includes('update') || action?.includes('UPDATE') || action?.includes('EDIT')) return 'bg-blue-50 text-blue-700';
    if (action?.includes('login') || action?.includes('LOGIN')) return 'bg-purple-50 text-purple-700';
    return 'bg-zinc-100 text-zinc-700';
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h2 className="text-lg font-semibold text-zinc-900">Activity Log</h2>
        <Button variant="outline" onClick={handleExportAuditLogs}>
          <Download className="mr-2 h-4 w-4" />
          Export CSV
        </Button>
      </div>
      
      {/* Filters */}
      <Card className="border-zinc-100">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <Label className="text-xs text-zinc-500">Action</Label>
              <Select value={auditFilter.action} onValueChange={(v) => setAuditFilter({ ...auditFilter, action: v })}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="All actions" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  {auditActions.map(action => (
                    <SelectItem key={action} value={action}>
                      {action.replace('.', ' ').replace('_', ' ')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-zinc-500">User</Label>
              <Select value={auditFilter.userId} onValueChange={(v) => setAuditFilter({ ...auditFilter, userId: v })}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="All users" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  {users.map(user => (
                    <SelectItem key={user.id} value={user.id}>{user.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-zinc-500">Start Date</Label>
              <input
                type="date"
                value={auditFilter.startDate}
                onChange={(e) => setAuditFilter({ ...auditFilter, startDate: e.target.value })}
                className="mt-1 w-full h-10 px-3 rounded-md border border-zinc-200 text-sm"
              />
            </div>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Label className="text-xs text-zinc-500">End Date</Label>
                <input
                  type="date"
                  value={auditFilter.endDate}
                  onChange={(e) => setAuditFilter({ ...auditFilter, endDate: e.target.value })}
                  className="mt-1 w-full h-10 px-3 rounded-md border border-zinc-200 text-sm"
                />
              </div>
              <Button onClick={fetchAuditLogs}>Filter</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logs List */}
      <Card className="border-zinc-100">
        <CardContent className="p-0 divide-y divide-zinc-100">
          {auditLogs.length === 0 ? (
            <div className="p-8 text-center text-zinc-500">
              <Calendar className="h-12 w-12 mx-auto mb-4 text-zinc-300" />
              <p>No activity logs found</p>
              <p className="text-sm mt-1">Adjust your filters or check back later</p>
            </div>
          ) : (
            auditLogs.map((log) => (
              <div key={log.id} className="p-4 hover:bg-zinc-50 transition-colors">
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-full bg-zinc-100 flex items-center justify-center">
                    <User className="h-5 w-5 text-zinc-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-zinc-900">{log.user_name || 'System'}</span>
                      <ChevronRight className="h-4 w-4 text-zinc-400" />
                      <Badge className={getActionBadgeColor(log.action)}>
                        {log.action?.replace(/_/g, ' ')}
                      </Badge>
                    </div>
                    {log.details && (
                      <p className="text-sm text-zinc-600 mt-1 line-clamp-2">
                        {typeof log.details === 'string' ? log.details : JSON.stringify(log.details)}
                      </p>
                    )}
                    <p className="text-xs text-zinc-400 mt-1">
                      {formatDateTime(log.created_at)}
                      {log.ip_address && ` • ${log.ip_address}`}
                    </p>
                  </div>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLogsTab;
